import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["depositsChart", "hourlyDepositsChart", "sumByHoursChart"]

  connect() {
    this.initializeCharts()
  }

  disconnect() {
    this.destroyCharts()
  }

  initializeCharts() {
    // Подождать загрузки Chart.js
    if (typeof Chart === 'undefined') {
      setTimeout(() => this.initializeCharts(), 100)
      return
    }

    this.destroyCharts()

    const chartData = JSON.parse(this.element.dataset.dailyResults || '[]')
    const hourlyChartData = JSON.parse(this.element.dataset.hourlyResults || '[]')
    const sumByHoursData = JSON.parse(this.element.dataset.sumByHours || '[]')

    this.createDepositsChart(chartData)
    this.createHourlyDepositsChart(hourlyChartData)
    this.createSumByHoursChart(sumByHoursData)
  }

  destroyCharts() {
    if (this.depositsChart) {
      this.depositsChart.destroy()
      this.depositsChart = null
    }
    if (this.hourlyDepositsChart) {
      this.hourlyDepositsChart.destroy()
      this.hourlyDepositsChart = null
    }
    if (this.sumByHoursChart) {
      this.sumByHoursChart.destroy()
      this.sumByHoursChart = null
    }
  }

  createDepositsChart(chartData) {
    const ctx = this.depositsChartTarget
    if (!ctx) return

    const sortedData = chartData.sort((a, b) => new Date(a.day) - new Date(b.day))

    const labels = sortedData.map(item => {
      const date = new Date(item.day)
      return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })
    })

    const totalCounts = sortedData.map(item => item.total_count)
    const successCounts = sortedData.map(item => item.success_count)
    const conversions = sortedData.map(item => item.conversion)

    this.depositsChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [
          {
            label: 'Всего депозитов',
            data: totalCounts,
            backgroundColor: 'rgba(59, 130, 246, 0.8)',
            borderColor: 'rgba(59, 130, 246, 1)',
            borderWidth: 1,
            yAxisID: 'y'
          },
          {
            label: 'Успешных депозитов',
            data: successCounts,
            backgroundColor: 'rgba(34, 197, 94, 0.8)',
            borderColor: 'rgba(34, 197, 94, 1)',
            borderWidth: 1,
            yAxisID: 'y'
          },
          {
            label: 'Конверсия (%)',
            data: conversions,
            type: 'line',
            borderColor: 'rgba(239, 68, 68, 1)',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            borderWidth: 3,
            fill: false,
            tension: 0.4,
            yAxisID: 'y1',
            pointBackgroundColor: 'rgba(239, 68, 68, 1)',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 6
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        plugins: {
          title: {
            display: true,
            text: 'Статистика депозитов по дням',
            font: {
              size: 16,
              weight: 'bold'
            },
            align: 'start'
          },
          legend: {
            position: 'top',
            labels: {
              usePointStyle: true,
              padding: 20
            }
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                let label = context.dataset.label || ''
                if (label) {
                  label += ': '
                }
                if (context.datasetIndex === 2) { // Конверсия
                  label += context.parsed.y.toFixed(1) + '%'
                } else {
                  label += context.parsed.y
                }
                return label
              }
            }
          }
        },
        scales: {
          x: {
            display: true,
            title: {
              display: true,
              text: 'Дата'
            },
            grid: {
              display: false
            },
          },
          y: {
            type: 'linear',
            display: true,
            position: 'left',
            title: {
              display: true,
              text: 'Количество депозитов'
            },
            grid: {
              color: 'rgba(0, 0, 0, 0.1)'
            },
            max: Math.ceil(Math.max(...totalCounts) * 1.1)
          },
          y1: {
            type: 'linear',
            display: true,
            position: 'right',
            title: {
              display: true,
              text: 'Конверсия (%)'
            },
            grid: {
              drawOnChartArea: false,
            },
            min: -10,
            max: 110
          }
        }
      }
    })
  }

  createHourlyDepositsChart(hourlyChartData) {
    const ctx = this.hourlyDepositsChartTarget
    if (!ctx) return

    const sortedData = hourlyChartData.sort((a, b) => new Date(a.hour) - new Date(b.hour))

    const labels = sortedData.map(item => {
      const date = new Date(item.hour)
      return date.toLocaleString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: '2-digit'
      })
    })

    const totalCounts = sortedData.map(item => item.total_count)
    const successCounts = sortedData.map(item => item.success_count)
    const conversions = sortedData.map(item => item.conversion)

    this.hourlyDepositsChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [
          {
            label: 'Всего депозитов',
            data: totalCounts,
            backgroundColor: 'rgba(147, 51, 234, 0.8)',
            borderColor: 'rgba(147, 51, 234, 1)',
            borderWidth: 1,
            yAxisID: 'y'
          },
          {
            label: 'Успешных депозитов',
            data: successCounts,
            backgroundColor: 'rgba(16, 185, 129, 0.8)',
            borderColor: 'rgba(16, 185, 129, 1)',
            borderWidth: 1,
            yAxisID: 'y'
          },
          {
            label: 'Конверсия (%)',
            data: conversions,
            type: 'line',
            borderColor: 'rgba(245, 101, 101, 1)',
            backgroundColor: 'rgba(245, 101, 101, 0.1)',
            borderWidth: 3,
            fill: false,
            tension: 0.4,
            yAxisID: 'y1',
            pointBackgroundColor: 'rgba(245, 101, 101, 1)',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 6
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        plugins: {
          title: {
            display: true,
            text: 'Статистика депозитов по часам',
            font: {
              size: 16,
              weight: 'bold'
            },
            align: 'start'
          },
          legend: {
            position: 'top',
            labels: {
              usePointStyle: true,
              padding: 20
            }
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                let label = context.dataset.label || ''
                if (label) {
                  label += ': '
                }
                if (context.datasetIndex === 2) { // Конверсия
                  label += context.parsed.y.toFixed(1) + '%'
                } else {
                  label += context.parsed.y
                }
                return label
              }
            }
          }
        },
        scales: {
          x: {
            display: true,
            title: {
              display: true,
              text: 'Время'
            },
            grid: {
              display: false
            },
          },
          y: {
            type: 'linear',
            display: true,
            position: 'left',
            title: {
              display: true,
              text: 'Количество депозитов'
            },
            grid: {
              color: 'rgba(0, 0, 0, 0.1)'
            },
            max: Math.ceil(Math.max(...totalCounts) * 1.1)
          },
          y1: {
            type: 'linear',
            display: true,
            position: 'right',
            title: {
              display: true,
              text: 'Конверсия (%)'
            },
            grid: {
              drawOnChartArea: false,
            },
            min: -10,
            max: 110
          }
        }
      }
    })
  }

  createSumByHoursChart(sumByHoursData) {
    const ctx = this.sumByHoursChartTarget
    if (!ctx) return

    const sortedData = sumByHoursData.sort((a, b) => new Date(a.hour) - new Date(b.hour))

    const labels = sortedData.map(item => {
      const date = new Date(item.hour)
      return date.toLocaleString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: '2-digit'
      })
    })

    const totalAmounts = sortedData.map(item => item.total_amount)
    const successAmounts = sortedData.map(item => item.success_amount)

    this.sumByHoursChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [
          {
            label: 'Сумма всех транзакций (RUB)',
            data: totalAmounts,
            backgroundColor: 'rgba(59, 130, 246, 0.8)',
            borderColor: 'rgba(59, 130, 246, 1)',
            borderWidth: 1,
            yAxisID: 'y'
          },
          {
            label: 'Сумма успешных транзакций (RUB)',
            data: successAmounts,
            backgroundColor: 'rgba(34, 197, 94, 0.8)',
            borderColor: 'rgba(34, 197, 94, 1)',
            borderWidth: 1,
            yAxisID: 'y'
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        plugins: {
          title: {
            display: true,
            text: 'Сумма по успешным и всем транзакциям по часам',
            font: {
              size: 16,
              weight: 'bold'
            },
            align: 'start'
          },
          legend: {
            position: 'top',
            labels: {
              usePointStyle: true,
              padding: 20
            }
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                let label = context.dataset.label || ''
                if (label) {
                  label += ': '
                }
                label += context.parsed.y.toFixed(2) + ' RUB'
                return label
              }
            }
          }
        },
        scales: {
          x: {
            display: true,
            title: {
              display: true,
              text: 'Время'
            },
            grid: {
              display: false
            },
          },
          y: {
            type: 'linear',
            display: true,
            position: 'left',
            title: {
              display: true,
              text: 'Сумма (RUB)'
            },
            grid: {
              color: 'rgba(0, 0, 0, 0.1)'
            },
            max: Math.ceil(Math.max(...totalAmounts) * 1.1)
          }
        }
      }
    })
  }
}
